import { useEffect, useState } from "react";
import { getTigerResource } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Shown while the UI establishes the replay-server session (the
 * `replay_server_session` path). Port of the C++ `SERVER_REPLAY`
 * `Ui_progress_info_dialog` (view shown in `end_register_process_tigerui`,
 * stopped in `Ti_server_replay_session_reply` once the session is handled).
 *
 * Opens on `server-replay-progress` `true` (emitted before
 * `ti_server_replay_session`) and closes on `false` (emitted when the reply is
 * handled). Like C++ it has no cancel — the backend retries until the session is
 * established. Mounted once.
 */
export function ServerReplayDialog() {
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    let off: (() => void) | undefined;
    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const unlisten = await listen<boolean>("server-replay-progress", (e) => {
        setConnecting(e.payload);
      });
      if (cancelled) unlisten();
      else off = unlisten;
    });
    return () => {
      cancelled = true;
      off?.();
    };
  }, []);

  if (!connecting) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <span style={styles.title}>{getTigerResource("TIGER_SERVER_TITLE")}</span>
        </div>
        <div style={styles.body}>
          <div style={styles.msg}>{getTigerResource("TIGER_SERVER_MSG")}</div>
          <div style={styles.barOuter}>
            <div style={styles.barIndet} />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: { ...acrylicStyles.overlay, display: "flex", alignItems: "center", justifyContent: "center" },
  dialog: {
    background: "var(--ui-surface)", backdropFilter: "var(--ui-surface-blur)", WebkitBackdropFilter: "var(--ui-surface-blur)",
    border: "1px solid rgba(255,255,255,0.65)", borderRadius: 14, width: 380, maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)", overflow: "hidden" as const,
    display: "flex", flexDirection: "column" as const, fontFamily: acrylicTokens.font, color: acrylicTokens.textPrimary,
  },
  // TIGER_SERVER_MSG embeds a newline ("Please wait...\n Connecting…").
  msg: { fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-line" as const, color: acrylicTokens.textSecondary },
  barOuter: { position: "relative" as const, height: 8, background: "rgba(255,255,255,0.85)", border: "1px solid rgba(120,130,150,0.35)", borderRadius: 6, overflow: "hidden" },
  barIndet: { position: "absolute" as const, left: 0, top: 0, bottom: 0, width: "40%", background: "var(--ui-btn-primary-bg)", animation: "tiger-indet 1.2s ease-in-out infinite" },
} as const;
